import type { APIFieldError } from '@/lib/api/apiTypes';

type APIErrorLike = Error & {
  status: number;
  code?: string;
  fieldErrors?: APIFieldError[];
};

type ErrorCopyContext =
  | 'avatar_upload'
  | 'comment'
  | 'deal'
  | 'event'
  | 'event_editor'
  | 'flyer'
  | 'poster_upload'
  | 'profile'
  | 'rsvp'
  | 'save'
  | 'venue'
  | 'venue_picker'
  | 'workspace';

const GENERIC_RECOVERY_COPY = 'Something went wrong. Try again in a moment.';
const READ_ONLY_MARKET_ERROR_CODE = 'read_only_market';
const READ_ONLY_MARKET_ERROR_MESSAGE =
  'Radious is in read-only mode outside our launch market. You can browse, but joining and saving events unlock when Radious opens in your city.';

const CODE_COPY: Record<string, string> = {
  auth_missing: 'Sign in to continue.',
  session_expired: 'Your session expired. Sign in again to continue.',
  invalid_token: 'Sign in again to continue.',
  unauthorized: 'Sign in to continue.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'We could not find that item.',
  conflict: 'That update conflicts with the current state. Refresh and try again.',
  rate_limited: 'Too many attempts. Wait a moment, then try again.',
  invalid_json: 'Check the details and try again.',
  invalid_input: 'Check the details and try again.',
  missing_required_field: 'Add the required details and try again.',
  invalid_field: 'Check the highlighted details and try again.',
  event_schedule_invalid: 'Check the event schedule and try again.',
  event_poster_required: 'Add at least one cover image.',
  event_archived: 'This event is no longer active.',
  event_at_capacity: 'This event is at capacity.',
  unsupported_upload_type: 'Use a JPG, PNG, WebP, or HEIC image.',
  upload_too_large: 'That image is too large. Try a smaller one.',
  upload_failed: 'Upload failed. Try again in a moment.',
  moderation_rejected: 'That upload needs a quick review before it can be used in Radious.',
  moderation_unavailable: 'Safety checks are busy right now. Try again in a minute.',
  storage_unavailable: 'Uploads are temporarily unavailable. Try again in a minute.',
  upstream_unavailable: 'Something on our side is temporarily unavailable. Try again in a minute.',
  internal_error: 'Something went wrong on our side. Try again in a minute.',
  read_only_market: READ_ONLY_MARKET_ERROR_MESSAGE,
};

const STATUS_COPY: Record<number, string> = {
  400: 'Check the details and try again.',
  401: 'Sign in to continue.',
  403: 'You do not have permission to do that.',
  404: 'We could not find that item.',
  409: 'That update conflicts with the current state. Refresh and try again.',
  422: 'Check the details and try again.',
  429: 'Too many attempts. Wait a moment, then try again.',
  500: 'Something went wrong on our side. Try again in a minute.',
  502: 'Something on our side is temporarily unavailable. Try again in a minute.',
  503: 'Something on our side is temporarily unavailable. Try again in a minute.',
};

const CONTEXT_COPY: Partial<Record<ErrorCopyContext, Partial<Record<string, string>>>> = {
  avatar_upload: {
    unsupported_upload_type: 'Use a JPG, PNG, WebP, or HEIC image for your profile photo.',
    upload_too_large: 'Profile photos must be 5 MB or smaller.',
    upload_failed: 'We could not upload that profile photo. Try again in a moment.',
  },
  comment: {
    invalid_input: 'Write a comment before posting.',
    not_found: 'That event or comment is no longer available.',
  },
  event_editor: {
    forbidden:
      'You cannot manage this venue event from this account. Switch to a teammate account for that venue, or post it personally instead.',
    invalid_field: 'One detail needs a quick fix before publishing. Check the highlighted fields and try again.',
    missing_required_field: 'Add the required event details and try again.',
    event_schedule_invalid: 'Check the schedule details and try again.',
  },
  flyer: {
    forbidden: 'Flyer assist is a Growth feature for venue teams.',
    unsupported_upload_type: 'Use a JPG, PNG, WebP, or HEIC flyer.',
    upload_too_large: 'That flyer is too large to read here. Try a smaller image or enter the event manually.',
    upstream_unavailable: 'We could not read that flyer. Add the event details manually and try again.',
  },
  poster_upload: {
    unsupported_upload_type: 'Use a JPG, PNG, WebP, or HEIC image for the poster.',
    upload_too_large: 'Poster images must be 10 MB or smaller.',
    upload_failed: 'We could not upload that poster. Try again in a moment.',
  },
  rsvp: {
    event_at_capacity: 'This event is at capacity.',
    event_archived: 'This event is no longer taking RSVPs.',
    conflict: 'Your RSVP changed elsewhere. Refresh and try again.',
  },
  save: {
    not_found: 'That item is no longer available to save.',
  },
  venue_picker: {
    not_found: 'No venues matched that search. Enter the venue manually.',
    upstream_unavailable: 'Venue search is temporarily unavailable. Enter the venue manually.',
  },
};

export function getAPIErrorRecoveryCopy(error: unknown, context?: ErrorCopyContext, fallback = GENERIC_RECOVERY_COPY) {
  if (!isAPIErrorLike(error)) {
    return fallback;
  }

  if (error.code === READ_ONLY_MARKET_ERROR_CODE || error.message === READ_ONLY_MARKET_ERROR_MESSAGE) {
    return READ_ONLY_MARKET_ERROR_MESSAGE;
  }

  const code = typeof error.code === 'string' ? error.code : undefined;
  if (code && context && CONTEXT_COPY[context]?.[code]) {
    return CONTEXT_COPY[context]?.[code] ?? fallback;
  }
  if (code && CODE_COPY[code]) {
    return CODE_COPY[code];
  }
  if (STATUS_COPY[error.status]) {
    return STATUS_COPY[error.status];
  }
  return fallback;
}

export function getAPIErrorFieldCopy(error: unknown, field: string, fallback?: string) {
  const fieldError = isAPIErrorLike(error)
    ? error.fieldErrors?.find((candidate) => candidate.field === field)
    : null;
  return fieldError?.message ?? fallback ?? null;
}

export function getFirstAPIErrorFieldCopy(error: unknown, fields: string[], fallback?: string) {
  if (!isAPIErrorLike(error)) {
    return fallback ?? null;
  }

  for (const field of fields) {
    const fieldCopy = getAPIErrorFieldCopy(error, field);
    if (fieldCopy) {
      return fieldCopy;
    }
  }

  return fallback ?? null;
}

function isAPIErrorLike(error: unknown): error is APIErrorLike {
  return error instanceof Error && typeof (error as { status?: unknown }).status === 'number';
}
